import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, CircularProgress, Link, Typography } from "@mui/material";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import AnimationCenterCropVideo from "../../../mobile-jigsaw-animation/components/AnimationCenterCropVideo";
import {
    appendScreenshotLang,
    slidingAnimationKey,
    useScreenshotTranslation,
} from "../../../../lib/screenshotI18n";
import { screenshotShellFontSx } from "../../../../lib/screenshotFonts";
import ScreenshotExpiredPage from "../../components/ScreenshotExpiredPage";
import ScreenshotPromoHeader from "../../components/ScreenshotPromoHeader";
import ScreenshotSizeDifficultyBar from "../../components/ScreenshotSizeDifficultyBar";
import ScreenshotTagBar from "../../components/ScreenshotTagBar";
import {
    getStoryTitle,
    getTemplateDifficultyLevel,
    getTemplateGrid,
    loadScreenshotSliding,
} from "../../lib/screenshotApi";
import { isScreenshotSceneExpired } from "../../lib/screenshotAccess";
import { SCREENSHOTS_MAIN_PATH } from "../../routes";
import {
    boardFrameSx,
    boardRegionSx,
    phoneShellSx,
    promoSubtitleSx,
    puzzleTitleBarSx,
    slidingScreenshotAccent,
} from "../../styles/screenshotStyles";
import { getAnimationScene } from "../animationScene";
import { animationBrandPath } from "../routes";

/**
 * App Store animation — center-cropped completion video in the same board slot as the puzzle.
 */
export default function AnimationVideoPage() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { t, lang } = useScreenshotTranslation();
    const scene = getAnimationScene(slug);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [group, setGroup] = useState(null);
    const [template, setTemplate] = useState(null);
    const navigatedToBrandRef = useRef(false);

    useEffect(() => {
        if (!scene) {
            setLoading(false);
            return undefined;
        }

        let cancelled = false;
        (async () => {
            setLoading(true);
            setError(null);
            try {
                const result = await loadScreenshotSliding(scene);
                if (cancelled) return;
                setGroup(result.group);
                setTemplate(result.template);
            } catch (e) {
                if (!cancelled) {
                    setError(e?.message ?? "Failed to load sliding puzzle");
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [scene]);

    const grid = useMemo(() => (template ? getTemplateGrid(template) : 0), [template]);
    const title = getStoryTitle(group?.story);
    const puzzleTags = useMemo(() => {
        const fromTemplate = group?.puzzleTemplate?.tags ?? [];
        if (fromTemplate.length > 0) return fromTemplate;
        return scene?.label ? [scene.label] : [];
    }, [group, scene?.label]);

    const handleCompletionVideoEnded = useCallback(() => {
        if (navigatedToBrandRef.current || !scene?.slug) return;
        navigatedToBrandRef.current = true;
        navigate(appendScreenshotLang(animationBrandPath(scene.slug), lang));
    }, [scene?.slug, lang, navigate]);

    if (isScreenshotSceneExpired(slug)) {
        return <ScreenshotExpiredPage />;
    }

    if (!scene?.completionVideoUrl) {
        return (
            <Box sx={{ ...phoneShellSx, p: 2 }}>
                <Typography color="error" sx={{ mb: 1 }}>
                    {t("mobileScreenshot.common.unknown-animation", { slug })}
                </Typography>
                <Link component={RouterLink} to={SCREENSHOTS_MAIN_PATH} sx={{ color: slidingScreenshotAccent }}>
                    {t("mobileScreenshot.common.back-to-index")}
                </Link>
            </Box>
        );
    }

    if (loading) {
        return (
            <Box sx={{ ...phoneShellSx, alignItems: "center", justifyContent: "center" }}>
                <CircularProgress size={32} sx={{ color: slidingScreenshotAccent }} />
            </Box>
        );
    }

    if (error || !template || grid <= 0) {
        return (
            <Box sx={{ ...phoneShellSx, p: 2 }}>
                <Typography color="error" sx={{ mb: 1 }}>
                    {error ?? t("mobileScreenshot.common.could-not-load")}
                </Typography>
                <Link component={RouterLink} to={SCREENSHOTS_MAIN_PATH} sx={{ color: slidingScreenshotAccent }}>
                    {t("mobileScreenshot.common.back-to-index")}
                </Link>
            </Box>
        );
    }

    return (
        <Box sx={{ ...phoneShellSx, ...screenshotShellFontSx(lang) }}>
            <ScreenshotPromoHeader
                promoHeadline={t(slidingAnimationKey(scene.slug, "headline"))}
                promoSubtitle={t(slidingAnimationKey(scene.slug, "subtitle"))}
            />

            <Box sx={boardRegionSx}>
                <Box
                    sx={{
                        ...boardFrameSx,
                        position: "relative",
                        width: "100%",
                        aspectRatio: "1 / 1",
                        overflow: "hidden",
                        bgcolor: "#000",
                    }}
                >
                    <AnimationCenterCropVideo
                        src={scene.completionVideoUrl}
                        sourceWidth={scene.completionVideoSourceWidth}
                        sourceHeight={scene.completionVideoSourceHeight}
                        cropSize={scene.completionVideoCropSize}
                        onEnded={handleCompletionVideoEnded}
                    />
                </Box>
            </Box>

            {(title || puzzleTags.length > 0) ? (
                <Box sx={puzzleTitleBarSx}>
                    {title ? (
                        <Typography component="p" sx={promoSubtitleSx}>
                            {title}
                        </Typography>
                    ) : null}
                    <ScreenshotTagBar tags={puzzleTags} />
                </Box>
            ) : null}

            <ScreenshotSizeDifficultyBar
                gridSize={grid}
                difficultyLevel={getTemplateDifficultyLevel(template)}
            />
        </Box>
    );
}
